import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from './useSession';
import { useOnboarding } from '../store/onboarding';
import { useWallets, serverToApp } from '../store/wallets';
import { fetchWallets } from '../lib/wallet';
import { mockWallets } from '../lib/mock';

export type GateStatus = 'loading' | 'onboarding' | 'app';

/** Decide the first screen by wallet existence: a user with a wallet on the
 *  server skips onboarding; a new user (no wallet) sees it. Also hydrates the
 *  wallets store from the server. Dev/browser (no session) uses the local
 *  onboarding flag + mock wallets. */
export function useWalletGate(): GateStatus {
  const { token, ready } = useSession();
  const localOnboarded = useOnboarding((s) => s.onboarded);
  const hydrate = useWallets((s) => s.hydrate);

  const q = useQuery({
    queryKey: ['wallets'],
    enabled: !!token,
    queryFn: () => fetchWallets(token!),
  });

  useEffect(() => {
    if (!token) hydrate(null, mockWallets);
    else if (q.data) hydrate(token, q.data.map(serverToApp));
  }, [token, q.data, hydrate]);

  if (!ready) return localOnboarded ? 'app' : 'onboarding';
  if (!token || q.isLoading) return 'loading';
  return (q.data?.length ?? 0) > 0 ? 'app' : 'onboarding';
}
